import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  alpha,
} from "@mui/material";

import DataTableCard from "../DataTableCard";
import ChoiceChipGroup from "../../atoms/ChoiceChipGroup";
import { WORKSPACE_ROOT_SX, WorkspaceBody } from "./_workspaceParts";
import { upcomingLabel, isSoon } from "../../../pages/Secretary/PatientsPage/patientListFormat";
import ConfirmDialog from "../../common/ConfirmDialog";
import AppointmentDetailDrawer from "./drawers/AppointmentDetailDrawer";
import { EventNote as EventNoteIcon } from "@mui/icons-material";
import { useClinicTheme } from "../../../theme";
import { useAlert } from "../../../context/AlertProvider";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import {
  useListPatientAppointmentsMutation,
  useUpdateAppointmentDetailsMutation,
  useDeleteAppointmentMutation,
  useListClinicDoctorsMutation,
} from "../../../store/secretaryApi";
import { useListLabTestsMutation } from "../../../store/systemVariablesApi";
import { staffLabel } from "../../../utils/staffName";
import useTerms from "../../../hooks/useTerms";

const STATUS_META = {
  scheduled: {
    label: "Planlandı",
    bg: `${COLORS.slate300}33`,
    fg: COLORS.slate700,
  },
  in_examination: {
    label: "Muayenede",
    bg: alpha(COLORS.warning, 0.12),
    fg: COLORS.warningDarker,
  },
  cancelled: {
    label: "İptal",
    bg: alpha(COLORS.error, 0.08),
    fg: COLORS.errorDark,
  },
  completed: {
    label: "Tamamlandı",
    bg: alpha(COLORS.success, 0.08),
    fg: COLORS.successDark,
  },
  finished: {
    label: "Tamamlandı",
    bg: alpha(COLORS.success, 0.08),
    fg: COLORS.successDark,
  },
  no_show: {
    label: "Gelmedi",
    bg: alpha(COLORS.warning, 0.12),
    fg: COLORS.warningDarker,
  },
  encounter: {
    label: "Görüşme",
    bg: `${COLORS.slate300}33`,
    fg: COLORS.slate700,
  },
};

const TYPE_LABELS = {
  ilk_muayene: "İlk Muayene",
  kontrol: "Kontrol",
};

const segmentsFor = (terms) => [
  { value: "all", label: "Tümü", empty: "" },
  { value: "upcoming", label: "Yaklaşan", empty: `Bu ${terms.patientGenLower} ileri tarihli randevusu yok.` },
  { value: "past", label: "Geçmiş", empty: `Bu ${terms.patientGenLower} geçmiş randevusu yok.` },
  { value: "cancelled", label: "İptal", empty: "İptal edilmiş randevu yok." },
];

// Düz liste tablosu (design-principles §3) — Hastalar tablosuyla aynı ölçüler
const headSx = {
  fontWeight: 600,
  fontSize: TYPO.caption,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: COLORS.slate500,
  bgcolor: COLORS.white,
  borderBottom: `1px solid ${COLORS.slate200}`,
  py: 1.25,
  whiteSpace: "nowrap",
};
const cellSx = { py: 1.25, borderBottom: `1px solid ${COLORS.slate100}` };
const mainText = { fontSize: TYPO.body, fontWeight: 500, color: COLORS.slate800, lineHeight: 1.35 };
const subText = { fontSize: TYPO.caption, color: COLORS.slate500, lineHeight: 1.35 };

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (_) {
    return "—";
  }
};

const formatTime = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return "";
  }
};

/**
 * AppointmentsTab — hastanın randevuları: düz liste (hazır filtre + tablo),
 * satır tıklanınca detay drawer'ı. Kurallar: docs/design-principles.md §3.
 *
 * Appointment creation is handled via the calendar page (schedule validation
 * is heavy); this tab supports editing complaint/notes on an existing
 * appointment and deleting cancelled appointments. Muayene Et shortcut stays
 * inside the drawer.
 *
 * @property {"standalone" | "session"} mode
 * @property {string} patientId
 * @property {string} [clinicId]      Admin/AdminStaff context — the clinic the
 *   patient is being viewed under. ClinicStaff callers ignore this (server
 *   always uses their own clinic).
 * @property {string} token
 * @property {boolean} [canEdit=true]
 * @property {object} [initialData]   Session mode payload (e.g. { list, patient })
 * @property {(promise: Promise) => Promise} [registerAutoSave]
 * @property {(status: "idle"|"saving"|"saved"|"error") => void} [onSaveStatusChange]
 */
export default function AppointmentsTab({
  mode,
  patientId,
  clinicId,
  token,
  canEdit = true,
  initialData,
  registerAutoSave,
  onSaveStatusChange,
}) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const { showSuccess, showError } = useAlert();

  const [listPatientAppointments, { isLoading: isLoadingList }] =
    useListPatientAppointmentsMutation();
  const [updateAppointmentDetails] = useUpdateAppointmentDetailsMutation();
  const [deleteAppointment] = useDeleteAppointmentMutation();
  const [listClinicDoctors] = useListClinicDoctorsMutation();
  const [listLabTests] = useListLabTestsMutation();

  const [appointments, setAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [labTests, setLabTests] = useState([]);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [patientSubtitle, setPatientSubtitle] = useState("");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [segment, setSegment] = useState("all");
  const [sortDir, setSortDir] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // ── Initial hydrate ─────────────────────────────────────────────
  useEffect(() => {
    if (mode === "session") {
      setAppointments(
        Array.isArray(initialData?.list) ? initialData.list : []
      );
      setDoctors(
        Array.isArray(initialData?.doctors) ? initialData.doctors : []
      );
      setLabTests(
        Array.isArray(initialData?.labTests) ? initialData.labTests : []
      );
      setPatientSubtitle(
        initialData?.patient
          ? `${initialData.patient.name || ""} ${initialData.patient.surname || ""}`.trim()
          : ""
      );
      setInitialLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    initialData?.list,
    initialData?.doctors,
    initialData?.labTests,
    initialData?.patient,
  ]);

  const refresh = useCallback(async () => {
    if (mode !== "standalone" || !token || !patientId) return;
    try {
      const [apptRes, doctorsRes, labRes] = await Promise.all([
        listPatientAppointments({ token, patientId, clinicId }).unwrap(),
        listClinicDoctors({ token, clinicId }).unwrap(),
        listLabTests({ token, status: "active", limit: 200 }).unwrap(),
      ]);
      setAppointments(apptRes?.data?.appointments || []);
      setDoctors(doctorsRes?.data?.doctors || []);
      setLabTests(labRes?.data?.items || []);
    } catch (err) {
      showError("Randevular yüklenemedi.");
    } finally {
      setInitialLoaded(true);
    }
  }, [
    mode,
    token,
    patientId,
    clinicId,
    listPatientAppointments,
    listClinicDoctors,
    listLabTests,
    showError,
  ]);

  useEffect(() => {
    if (mode === "standalone") {
      refresh();
    }
  }, [mode, refresh]);

  // ── Helpers ─────────────────────────────────────────────────────
  const doctorById = useMemo(() => {
    const map = new Map();
    (doctors || []).forEach((d) => map.set(String(d._id), d));
    return map;
  }, [doctors]);

  const getDoctorName = useCallback(
    (doctorId) => {
      const doc = doctorById.get(String(doctorId || ""));
      return doc ? staffLabel(doc) || "—" : "—";
    },
    [doctorById]
  );

  // ── Mutation runner ─────────────────────────────────────────────
  const runMutation = async (mutationThunk) => {
    onSaveStatusChange?.("saving");
    try {
      const promise = mutationThunk();
      const wrapped = registerAutoSave ? registerAutoSave(promise) : promise;
      const result = await wrapped;
      onSaveStatusChange?.("saved");
      return result;
    } catch (err) {
      onSaveStatusChange?.("error");
      throw err;
    }
  };

  // ── Drawer handlers ─────────────────────────────────────────────
  const openDetail = (appt) => {
    setSelected(appt);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelected(null);
  };

  // Session mode: local state not updated after mutations; parent ExaminationSession
  // is expected to re-fetch examination payload via the autoSaveRegistry's
  // integration in Task 7. Do not mirror mutation responses here.
  const handleSave = async ({ appointmentId, complaint, notes }) => {
    if (!appointmentId) return;
    try {
      await runMutation(() =>
        updateAppointmentDetails({
          token,
          appointmentId,
          complaint,
          notes,
        }).unwrap()
      );
      closeDrawer();
      showSuccess("Randevu güncellendi.");
      if (mode === "standalone") await refresh();
    } catch (err) {
      showError("Randevu güncellenemedi.");
    }
  };

  const handleDelete = () => {
    if (!selected?._id) return;
    setPendingDelete(selected);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (deleteLoading) return;
    setDeleteConfirmOpen(false);
    setPendingDelete(null);
  };

  // Session mode: local state not updated after mutations; parent ExaminationSession
  // is expected to re-fetch examination payload via the autoSaveRegistry's
  // integration in Task 7. Do not mirror mutation responses here.
  const confirmDelete = async () => {
    if (!pendingDelete?._id) return;
    setDeleteLoading(true);
    try {
      await runMutation(() =>
        deleteAppointment({
          token,
          appointmentId: pendingDelete._id,
        }).unwrap()
      );
      setDeleteConfirmOpen(false);
      setPendingDelete(null);
      closeDrawer();
      showSuccess("Randevu silindi.");
      if (mode === "standalone") await refresh();
    } catch (err) {
      showError("Randevu silinemedi.");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Only cancelled appointments can be deleted (server-enforced).
  const canDeleteSelected =
    selected && String(selected.status || "").toLowerCase() === "cancelled";

  // ── Hazır filtre + sıralama + sayfalama (istemci tarafında) ───────
  const buckets = useMemo(() => {
    const now = Date.now();
    const out = { all: [], upcoming: [], past: [], cancelled: [] };
    (appointments || []).forEach((a) => {
      const status = String(a.status || "").toLowerCase();
      const t = a.startAt ? new Date(a.startAt).getTime() : 0;
      out.all.push(a);
      if (status === "cancelled") out.cancelled.push(a);
      else if (status === "scheduled" && t >= now) out.upcoming.push(a);
      else out.past.push(a);
    });
    return out;
  }, [appointments]);

  // Varsayılan: Yaklaşan'da en yakın önce, diğerlerinde en yeni önce
  const effectiveDir = sortDir || (segment === "upcoming" ? "asc" : "desc");
  const rows = useMemo(() => {
    const list = [...(buckets[segment] || [])];
    const sign = effectiveDir === "asc" ? 1 : -1;
    list.sort(
      (a, b) =>
        sign *
        ((a.startAt ? new Date(a.startAt).getTime() : 0) -
          (b.startAt ? new Date(b.startAt).getTime() : 0))
    );
    return list;
  }, [buckets, segment, effectiveDir]);
  const pageRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const segmentOptions = segmentsFor(terms).map((sg) => ({
    value: sg.value,
    label: `${sg.label} · ${buckets[sg.value].length}`,
  }));

  const showLoading = mode === "standalone" && !initialLoaded && isLoadingList;
  const emptyMessage =
    appointments.length === 0
      ? `Randevular takvimden oluşturulur; bu ${terms.patientDatLower} randevu verildiğinde burada listelenir.`
      : segmentsFor(terms).find((sg) => sg.value === segment)?.empty;

  const doctorNameOf = (item) => {
    const doctorId = typeof item.doctor === "object" ? item.doctor?._id : item.doctor;
    return doctorById.get(String(doctorId || ""))
      ? getDoctorName(doctorId)
      : staffLabel({ doctorRoleName: item?.snapshot?.doctorRoleName, name: item?.snapshot?.doctorName }) || "—";
  };

  return (
    <Box sx={WORKSPACE_ROOT_SX}>
      <WorkspaceBody title="Randevular" count={appointments.length}>
        {/* Hazır filtreler */}
        <Box sx={{ px: { xs: 2, md: 4 }, py: 1.5, flexShrink: 0 }}>
          <ChoiceChipGroup
            ariaLabel="Randevu filtresi"
            options={segmentOptions}
            value={segment}
            onChange={(v) => {
              setSegment(v);
              setSortDir(null);
              setPage(0);
            }}
          />
        </Box>

        <DataTableCard
      loading={Boolean(showLoading)}
          variant="flat"
          empty={!showLoading && rows.length === 0}
          emptyIcon={<EventNoteIcon />}
          emptyTitle={appointments.length === 0 ? "Henüz randevu yok" : "Bu filtrede randevu yok"}
          emptyMessage={emptyMessage}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={rows.length}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        >
          <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 820 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ ...headSx, pl: { xs: 2, md: 4 }, width: "22%" }}
                  sortDirection={effectiveDir}
                >
                  <TableSortLabel
                    active
                    direction={effectiveDir}
                    onClick={() => setSortDir(effectiveDir === "asc" ? "desc" : "asc")}
                    sx={{ "&.Mui-active": { color: COLORS.slate800 }, "& .MuiTableSortLabel-icon": { fontSize: 16 } }}
                  >
                    Tarih
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...headSx, width: "24%" }}>{terms.doctor}</TableCell>
                <TableCell sx={{ ...headSx, width: "13%" }}>Tür</TableCell>
                <TableCell sx={{ ...headSx, width: "27%" }}>Şikayet</TableCell>
                <TableCell sx={{ ...headSx, width: "14%", pr: { xs: 2, md: 4 } }}>Durum</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {showLoading ? (
                [...Array(6)].map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {[0, 1, 2, 3, 4].map((c) => (
                      <TableCell key={c} sx={{ ...cellSx, ...(c === 0 ? { pl: { xs: 2, md: 4 } } : {}) }}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length ? (
                pageRows.map((item) => {
                  const statusKey = String(item.status || "scheduled").toLowerCase();
                  const statusMeta =
                    statusKey === "in_examination"
                      ? { ...STATUS_META.in_examination, label: terms.inExam }
                      : STATUS_META[statusKey] || STATUS_META.scheduled;
                  const typeLabel =
                    ({ ...TYPE_LABELS, ilk_muayene: terms.firstVisitTitle })[String(item.appointmentType || "").toLowerCase()] || "Randevu";
                  const upcoming = statusKey === "scheduled" && item.startAt && new Date(item.startAt).getTime() >= Date.now();
                  const next = upcoming ? upcomingLabel(item.startAt) : null;
                  return (
                    <TableRow
                      key={item._id}
                      hover
                      onClick={() => openDetail(item)}
                      tabIndex={0}
                      aria-label={`Randevu detayını aç: ${formatDate(item.startAt)}`}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openDetail(item);
                        }
                      }}
                      sx={{ cursor: "pointer", "&:hover": { bgcolor: `${alpha(palette.primary, 0.03)} !important` } }}
                    >
                      <TableCell sx={{ ...cellSx, pl: { xs: 2, md: 4 } }}>
                        {next ? (
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              px: 1,
                              py: 0.25,
                              borderRadius: 1.5,
                              fontSize: TYPO.label,
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              ...(isSoon(item.startAt)
                                ? { bgcolor: alpha(palette.primary, 0.1), color: palette.primary }
                                : { bgcolor: COLORS.slate100, color: COLORS.slate700 }),
                            }}
                          >
                            {next}
                          </Box>
                        ) : (
                          <Typography sx={{ ...mainText, fontVariantNumeric: "tabular-nums" }} noWrap>
                            {formatDate(item.startAt)}
                            <Box component="span" sx={{ color: COLORS.slate500, ml: 0.75 }}>
                              {formatTime(item.startAt)}
                            </Box>
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <Typography sx={mainText} noWrap>{doctorNameOf(item)}</Typography>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <Typography sx={{ ...mainText, color: COLORS.slate600 }} noWrap>{typeLabel}</Typography>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {item.complaint ? (
                          <Typography sx={{ ...subText, fontSize: TYPO.label }} noWrap title={item.complaint}>
                            {item.complaint}
                          </Typography>
                        ) : (
                          <Typography sx={{ ...subText, color: COLORS.slate300 }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, pr: { xs: 2, md: 4 } }}>
                        <Chip
                          label={statusMeta.label}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "0.68rem",
                            borderRadius: 999,
                            bgcolor: statusMeta.bg,
                            color: statusMeta.fg,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={5} sx={{ height: 1, py: 0, border: 0 }} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DataTableCard>
      </WorkspaceBody>

      <AppointmentDetailDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        appointment={selected}
        getDoctorName={getDoctorName}
        labTests={labTests}
        subtitle={patientSubtitle || undefined}
        canEdit={canEdit}
        onSave={canEdit ? handleSave : null}
        onDelete={canEdit && canDeleteSelected ? handleDelete : null}
        token={token}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={closeDeleteConfirm}
        onConfirm={confirmDelete}
        title="Randevuyu Sil"
        message="Bu randevuyu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
        confirmText="Sil"
        cancelText="İptal"
        confirmVariant="error"
        loading={deleteLoading}
        loadingText="Siliniyor..."
      />
    </Box>
  );
}
